import { useTranslation } from 'react-i18next'
import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Field } from 'react-final-form'
import styled from 'styled-components'
import isEqual from 'lodash/isEqual'

import { usePrevious } from 'utils/hooks'
import filterColumnTypes from 'constants/filterColumnTypes'

import InputField from 'components/Shared/InputField/Index'
import Select from 'components/Shared/Select/Index'

import { COMPARISON_OPERATORS, DATE_COMPARISON_ALLOWED_VALUES } from 'constants/constants'

const PORTAL_Z_INDEX = 10001
const VALUES_SEPARATOR = ', '

const StyledSelect = styled(Select)`
  min-width: 140px;
`

const parseValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(VALUES_SEPARATOR)
  } else {
    return value
  }
}

const getSelectOptions = (filterField, operator, selectValues) => {
  const fieldType = filterField?.fieldType || ''
  const isDatetimeColumn = Boolean(
    (fieldType === filterColumnTypes.DATETIME) ||
    (fieldType === filterColumnTypes.DATE)
  )
  const isComparisonOperator = COMPARISON_OPERATORS.includes(operator)

  const allowedValues = (isDatetimeColumn && isComparisonOperator)
    ? DATE_COMPARISON_ALLOWED_VALUES
    : (filterField?.allowedValues || [])

  let options = []
  const allowedValuesMap = {}
  allowedValues.forEach(allowedValue => {
    options.push({
      label: allowedValue.name,
      value: allowedValue.value,
      bgColor: allowedValue.color,
    })
    allowedValuesMap[allowedValue.value] = true
  })
  const isCreatedByColumn = Boolean(fieldType === filterColumnTypes.CREATEDBY)
  const isBondedColumn = Boolean(fieldType === filterColumnTypes.BONDED)
  const inOrNotInOperator = Boolean(operator === 'in' || operator === 'not_in')
  const isSelectValue = Boolean(
    (allowedValues.length > 0) &&
    (inOrNotInOperator || isDatetimeColumn || isCreatedByColumn || isBondedColumn)
  )
  if (!isSelectValue) {
    options = []
  } else {
    // add option(s) with value(s) if they don't exist in allowed values
    // e.g. if allowed values where changed
    const multiselectWithValue = filterField?.isMultiSelect && selectValues.length > 0
    if (multiselectWithValue) {
      selectValues.forEach(val => {
        if (allowedValuesMap[val]) {
          return
        }
        options.push({
          label: val,
          value: val,
        })
      })
    }
  }
  return options
}

const ValueComponent = ({ value, onChange, filterField, operator }) => {
  const { t } = useTranslation()
  const selectValues = useMemo(() => {
    return value ? (value.split?.(VALUES_SEPARATOR) || []) : []
  }, [value])
  const options = useMemo(() => {
    return getSelectOptions(filterField, operator, selectValues)
  }, [filterField, operator, selectValues])

  const isSelectValue = Boolean(options.length > 0)
  const componentType = filterField ? (isSelectValue ? 'select' : 'text') : null
  const prevComponentType = usePrevious(componentType)

  // Clear value when component type changes,
  // but only after filterField and componentType are set.
  // Otherwise it clears data in editing mode right after mounting
  useEffect(() => {
    if (prevComponentType && componentType) {
      onChange('')
    }
  }, [componentType]) // eslint-disable-line react-hooks/exhaustive-deps

  const emptyOrNotEmptyOperator = Boolean(operator === 'empty' || operator === 'not_empty')
  const hideComponent = emptyOrNotEmptyOperator

  useEffect(() => {
    if (hideComponent) {
      onChange('')
    }
  }, [hideComponent]) // eslint-disable-line react-hooks/exhaustive-deps

  if (hideComponent) {
    return null
  }

  if (!isSelectValue) {
    return (
      <InputField
        value={value}
        onChange={onChange}
        placeholder={t('ticketsPage.filterForm.selectValuePlaceholder')}
        dataTest='value'
      />
    )
  } else {
    return (
      <StyledSelect
        small
        value={selectValues}
        onChange={onChange}
        options={options}
        placeholder={t('ticketsPage.filterForm.selectValuePlaceholder')}
        usePortal
        portalZindex={PORTAL_Z_INDEX}
        dataTest='value'
        multiselect={filterField?.isMultiSelect}
      />
    )
  }
}

ValueComponent.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  filterField: PropTypes.object,
  operator: PropTypes.string,
}

const ValueField = ({ name, filterField, operator }) => {
  return (
    // without custom parse empty values are not passed to onSubmit() so API doesn't get cleared strings
    // it is required fot input fields
    // see https://github.com/final-form/react-final-form/issues/130#issuecomment-425482365
    <Field
      name={name}
      isEqual={isEqual}
      parse={parseValue}
    >
      {(props) => {
        const { input } = props
        return (
          <ValueComponent
            value={input.value}
            onChange={input.onChange}
            filterField={filterField}
            operator={operator}
          />
        )
      }}
    </Field>
  )
}

ValueField.propTypes = {
  name: PropTypes.string.isRequired,
  filterField: PropTypes.object,
  operator: PropTypes.string,
  input: PropTypes.object,
}

export default ValueField
